import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FlushPanelContent } from '@gamma/ui/layout';
import React, { useContext, useEffect } from 'react';
import { IGetDetectionTimelineSummary } from '../../../../../../queries';
import {
  Detection,
  DetectionAlertSummary,
} from '@investigator/shared/generated/graphql';

import {
  ErrorMessage,
  GraphQLReqStatus,
  LoadingSpinner,
} from '@gamma/ui/progress';
import { i18n } from '@investigator/shared/localization';
import { SlideDrawerContext } from '@investigator/shared/providers';
import { DetectionAlerts, RelatedDetectionRow } from './Components';
import {
  DetectionDrawer,
  DetectionDrawerActions,
} from '../../../../../../components/DetectionDrawer';
import { SeverityScore } from '@investigator/shared/components';

const { correlationChart } = i18n.pages.detections;
const { headings, useFilters, limitReaced } = correlationChart;

interface RelatedDetectionsAlertsProps {
  loading: boolean;
  error?: ErrorMessage;
  data?: IGetDetectionTimelineSummary;
  detection: Detection;
  suricataRuleText: string;
  selectedDetection?: Detection;
  setSelectedDetectionId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  chartDetections: {
    detectionSourceDetections?: DetectionAlertSummary[];
    currentDetectionDetections?: DetectionAlertSummary[];
    detectionDestinationDetections?: DetectionAlertSummary[];
    detectionSourceDestinationDetections?: DetectionAlertSummary[];
  };
  getDetectionsLoading: boolean;
}

export const LimitReachedText = () => (
  <VStack
    p={2}
    spacing={1}
    alignItems="center"
    borderTop="solid 1px"
    borderTopColor="border.1"
  >
    <Text>{limitReaced}</Text>
    <Text color="text.secondary">{useFilters}</Text>
  </VStack>
);

export const RelatedDetectionsAlerts = React.memo(
  ({
    loading,
    error,
    data,
    detection,
    chartDetections,
    suricataRuleText,
    selectedDetection,
    getDetectionsLoading,
    setSelectedDetectionId,
  }: RelatedDetectionsAlertsProps) => {
    const {
      portalType,
      setPortalType,
      setIsSlideDrawerOpen,
      setSlideDrawerBody,
      setSlideDrawerTitle,
      setSlideDrawerActions,
    } = useContext(SlideDrawerContext);

    const {
      detectionSourceDetections,
      currentDetectionDetections,
      detectionDestinationDetections,
      detectionSourceDestinationDetections,
    } = chartDetections;

    const isCurrentDetectionOpen =
      !!chartDetections?.currentDetectionDetections?.find(
        (detection) =>
          detection.detection_id === selectedDetection?.detection_id,
      );

    const isDetectionSourceDestinationOpen =
      !!chartDetections?.detectionSourceDestinationDetections?.find(
        (detection) =>
          detection.detection_id === selectedDetection?.detection_id,
      );

    const isDetectionDestinationOpen =
      !!chartDetections?.detectionDestinationDetections?.find(
        (detection) =>
          detection.detection_id === selectedDetection?.detection_id,
      );

    const isDetectionSourceOpen =
      !!chartDetections?.detectionSourceDetections?.find(
        (detection) =>
          detection.detection_id === selectedDetection?.detection_id,
      );

    const defaultIndex = isCurrentDetectionOpen
      ? 0
      : isDetectionSourceDestinationOpen
        ? 1
        : isDetectionSourceOpen
          ? 2
          : isDetectionDestinationOpen
            ? 3
            : undefined;

    const groupings = [
      ...(currentDetectionDetections && currentDetectionDetections?.length > 0
        ? [0]
        : []),
      ...(detectionSourceDestinationDetections &&
      detectionSourceDestinationDetections?.length > 0
        ? [1]
        : []),
      ...(detectionSourceDetections && detectionSourceDetections?.length > 0
        ? [2]
        : []),
      ...(detectionDestinationDetections &&
      detectionDestinationDetections?.length > 0
        ? [3]
        : []),
    ];

    const index =
      defaultIndex !== undefined ? groupings.indexOf(defaultIndex) : undefined;

    useEffect(() => {
      if (
        detection.detection_id === selectedDetection?.detection_id &&
        portalType !== 'notes' &&
        portalType !== 'baseline' &&
        portalType !== 'alert'
      ) {
        setPortalType?.(undefined);
        setIsSlideDrawerOpen?.(false);
      }
    }, [
      detection.detection_id,
      selectedDetection?.detection_id,
      setIsSlideDrawerOpen,
      setPortalType,
      portalType,
    ]);

    return loading || error ? (
      <GraphQLReqStatus error={error} loading={loading} />
    ) : (
      <FlushPanelContent mt={0}>
        <HStack
          spacing={0}
          minH="750px"
          alignItems="start"
          borderTop="solid 1px"
          borderTopColor="border.1"
        >
          <Flex w="280px" borderRight="solid 1px" borderRightColor="border.1">
            <VStack w="100%">
              <Accordion w="100%" key={index} defaultIndex={index}>
                {currentDetectionDetections &&
                  currentDetectionDetections?.length > 0 && (
                    <AccordionItem bgColor="transparent">
                      <AccordionButton w="100%" justifyContent="space-between">
                        <Text as="span">{headings.currentDetection}</Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel p={0}>
                        <Box maxH={'770px'} overflowY="auto">
                          {currentDetectionDetections?.map(
                            (detection, index) => (
                              <RelatedDetectionRow
                                key={index}
                                item={{
                                  ...detection,
                                  detection_id: detection.detection_id ?? '',
                                  alert_name: detection.alert_name ?? '',
                                  entity_name: detection.entity_name ?? '',
                                  severity: detection.severity ?? 0,
                                }}
                                selectedDetection={selectedDetection}
                                setSelectedDetectionId={setSelectedDetectionId}
                              />
                            ),
                          )}
                          {(data?.getCurrentDetectionTimelineSummary
                            ?.row_count ?? 0) > 5 && <LimitReachedText />}
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                {detectionSourceDestinationDetections &&
                  detectionSourceDestinationDetections?.length > 0 && (
                    <AccordionItem bgColor="transparent">
                      <AccordionButton w="100%" justifyContent="space-between">
                        <Text as="span">{headings.sourceDest}</Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel p={0}>
                        <Box maxH={'770px'} overflowY="auto">
                          {detectionSourceDestinationDetections?.map(
                            (detection, index) => (
                              <RelatedDetectionRow
                                key={index}
                                item={{
                                  ...detection,
                                  detection_id: detection.detection_id ?? '',
                                  alert_name: detection.alert_name ?? '',
                                  entity_name: detection.entity_name ?? '',
                                  severity: detection.severity ?? 0,
                                }}
                                selectedDetection={selectedDetection}
                                setSelectedDetectionId={setSelectedDetectionId}
                              />
                            ),
                          )}
                          {(data?.getDetectionSourceDestinationTimelineSummary
                            ?.row_count ?? 0) > 5 && <LimitReachedText />}
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                {detectionSourceDetections &&
                  detectionSourceDetections?.length > 0 && (
                    <AccordionItem bgColor="transparent">
                      <AccordionButton w="100%" justifyContent="space-between">
                        <Text as="span">{headings.source}</Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel p={0}>
                        <Box maxH={'770px'} overflowY="auto">
                          {detectionSourceDetections?.map(
                            (detection, index) => (
                              <RelatedDetectionRow
                                key={index}
                                item={{
                                  ...detection,
                                  detection_id: detection.detection_id ?? '',
                                  alert_name: detection.alert_name ?? '',
                                  entity_name: detection.entity_name ?? '',
                                  severity: detection.severity ?? 0,
                                }}
                                selectedDetection={selectedDetection}
                                setSelectedDetectionId={setSelectedDetectionId}
                              />
                            ),
                          )}
                          {(data?.getDetectionSourceTimelineSummary
                            ?.row_count ?? 0) > 5 && <LimitReachedText />}
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                {detectionDestinationDetections &&
                  detectionDestinationDetections?.length > 0 && (
                    <AccordionItem bgColor="transparent">
                      <AccordionButton w="100%" justifyContent="space-between">
                        <Text as="span">{headings.destination}</Text>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel p={0}>
                        <Box maxH={'770px'} overflowY="auto">
                          {detectionDestinationDetections?.map(
                            (detection, index) => (
                              <RelatedDetectionRow
                                key={index}
                                item={{
                                  ...detection,
                                  detection_id: detection.detection_id ?? '',
                                  alert_name: detection.alert_name ?? '',
                                  entity_name: detection.entity_name ?? '',
                                  severity: detection.severity ?? 0,
                                }}
                                selectedDetection={selectedDetection}
                                setSelectedDetectionId={setSelectedDetectionId}
                              />
                            ),
                          )}
                          {(data?.getDetectionDestinationTimelineSummary
                            ?.row_count ?? 0) > 5 && <LimitReachedText />}
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  )}
                {detectionSourceDestinationDetections &&
                  detectionSourceDestinationDetections?.length === 0 && (
                    <AccordionItem bgColor="transparent">
                      <Box px={4} py={2} color="text.secondary">
                        {headings.sourceDest} (N/A)
                      </Box>
                    </AccordionItem>
                  )}
                {detectionSourceDetections &&
                  detectionSourceDetections?.length === 0 && (
                    <AccordionItem bgColor="transparent">
                      <Box px={4} py={2} color="text.secondary">
                        {headings.source} (N/A)
                      </Box>
                    </AccordionItem>
                  )}
                {detectionDestinationDetections &&
                  detectionDestinationDetections?.length === 0 && (
                    <AccordionItem bgColor="transparent">
                      <Box px={4} py={2} color="text.secondary">
                        {headings.destination} (N/A)
                      </Box>
                    </AccordionItem>
                  )}
              </Accordion>
            </VStack>
          </Flex>
          <Flex flexGrow={1}>
            <VStack p={4} w="full" spacing={4} alignItems="start">
              <HStack w="full" justifyContent="space-between">
                <HStack>
                  {selectedDetection?.rank?.severity !== null &&
                    selectedDetection?.rank?.severity !== undefined && (
                      <SeverityScore score={selectedDetection.rank.severity} />
                    )}
                  <Text textStyle="h5">
                    {selectedDetection?.alert_info?.alert_name}
                  </Text>
                </HStack>
                <Button
                  size="xs"
                  variant="solid"
                  colorScheme="blue"
                  onClick={() => {
                    setPortalType?.('detection');
                    setSlideDrawerTitle?.(
                      `${selectedDetection?.alert_info?.alert_name} | ${selectedDetection?.alert_entity?.entity_name}`,
                    );
                    setSlideDrawerBody?.(
                      <DetectionDrawer
                        item={selectedDetection}
                        disableBorders={true}
                        refetchMain={true}
                        queryDetectionsPaginatedVariables={{
                          query: '',
                          size: 10,
                          offset: 0,
                          sort: [],
                        }}
                      />,
                    );
                    setSlideDrawerActions?.(
                      <Box w="100%" my={2}>
                        <DetectionDrawerActions
                          key={`${selectedDetection?.detection_id}-detection-portal-actions`}
                          detection={selectedDetection}
                          queryDetectionsPaginatedVariables={{
                            query: '',
                            size: 10,
                            offset: 0,
                            sort: [],
                          }}
                        />
                      </Box>,
                    );
                    setIsSlideDrawerOpen?.(true);
                  }}
                  isDisabled={
                    getDetectionsLoading ||
                    detection.detection_id === selectedDetection?.detection_id
                  }
                >
                  {correlationChart.previewDetection}
                </Button>
              </HStack>
              {getDetectionsLoading && (
                <Center pt={4} width="100%">
                  <LoadingSpinner />
                </Center>
              )}
              {selectedDetection && (
                <Box w="100%">
                  <DetectionAlerts
                    detectionData={selectedDetection}
                    suricataRuleText={suricataRuleText}
                  />
                </Box>
              )}
            </VStack>
          </Flex>
        </HStack>
      </FlushPanelContent>
    );
  },
);
